/** biome-ignore-all lint/complexity/noUselessFragments: <this is needed here> */

import {
  Button,
  FilterContainer,
  GroupedResidentsMultiSelect,
  GroupedUsersMultiSelect,
  MultiSelect,
  RangePicker
} from '@lmc/ui-kit'
import dayjs, { type Dayjs } from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import styled from 'styled-components'
import type {
  DurationDiscrepancyApi,
  ReconciliationFilters as ReconciliationFiltersType,
  ReconciliationSortField,
  ReconciliationStatusValue
} from '../../Reports/types/reconciliation'
import {
  getReconciliationRangeDisabledDate,
  isValidReconciliationDateRange,
  RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API,
  RECONCILIATION_MAX_PAST_MONTHS
} from './reconciliationUtils'

const RECONCILIATION_PAGE_SIZE = 10

export function useReconciliationFilters(nominative: string) {
  const defaultStart = dayjs().subtract(7, 'day')
  const defaultEnd = dayjs().subtract(1, 'day')

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    defaultStart,
    defaultEnd
  ])
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  )
  const [discrepancyFilter, setDiscrepancyFilter] = useState<string[]>([])
  const [teamMemberFilter, setTeamMemberFilter] = useState<string[]>([])
  const [serviceUserFilter, setServiceUserFilter] = useState<string[]>([])
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | undefined>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>('desc')
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const apiSortField = useMemo((): ReconciliationSortField | undefined => {
    if (!sortBy || !sortOrder) {
      return undefined
    }

    const m: Record<string, ReconciliationSortField> = {
      date: 'startTime',
      status: 'status',
      teamMember: 'teamMemberName',
      serviceUser: 'subjectName',
      eventType: 'instanceTypeName',
      plannedTime: 'plannedTimeDuration',
      actualTime: 'actualTimeDuration',
      durationDiscrepancy: 'durationDiscrepancy',
      warningCount: 'warnings',
      paidTime: 'paidTime',
      payRate: 'payRate',
      payAmount: 'payAmount'
    }
    return m[sortBy]
  }, [sortBy, sortOrder])

  const filters: ReconciliationFiltersType = useMemo(
    () => ({
      startTime: dateRange[0].startOf('day').toISOString(),
      endTime: dateRange[1].endOf('day').toISOString(),
      ...(statusFilter
        ? { status: statusFilter as ReconciliationStatusValue }
        : {}),
      ...(discrepancyFilter.length
        ? {
            durationDiscrepancy: discrepancyFilter
              .map((d) => RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API[d])
              .filter((x): x is DurationDiscrepancyApi => Boolean(x))
          }
        : {}),
      ...(teamMemberFilter.length ? { teamMemberIds: teamMemberFilter } : {}),
      ...(serviceUserFilter.length
        ? { serviceUserIds: serviceUserFilter }
        : {}),
      ...(eventTypeFilter.length ? { eventTypeIds: eventTypeFilter } : {}),
      ...(apiSortField !== undefined && sortOrder
        ? {
            sortField: apiSortField,
            sortDir: sortOrder === 'asc' ? (1 as const) : (-1 as const)
          }
        : {}),
      page,
      pageSize: RECONCILIATION_PAGE_SIZE
    }),
    [
      dateRange,
      statusFilter,
      discrepancyFilter,
      teamMemberFilter,
      serviceUserFilter,
      eventTypeFilter,
      apiSortField,
      sortOrder,
      page
    ]
  )

  const hasActiveFilters =
    !!statusFilter ||
    discrepancyFilter.length > 0 ||
    teamMemberFilter.length > 0 ||
    serviceUserFilter.length > 0 ||
    eventTypeFilter.length > 0

  const clearFilters = useCallback(() => {
    setStatusFilter(undefined)
    setDiscrepancyFilter([])
    setTeamMemberFilter([])
    setServiceUserFilter([])
    setEventTypeFilter([])
    setPage(1)
    setSelectedRowKeys([])
  }, [])

  return {
    filters,
    page,
    setPage,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    selectedRowKeys,
    setSelectedRowKeys,
    nominative,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    discrepancyFilter,
    setDiscrepancyFilter,
    teamMemberFilter,
    setTeamMemberFilter,
    serviceUserFilter,
    setServiceUserFilter,
    eventTypeFilter,
    setEventTypeFilter,
    hasActiveFilters,
    clearFilters
  }
}

export function ReconciliationFiltersBar({
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  discrepancyFilter,
  setDiscrepancyFilter,
  teamMemberFilter,
  setTeamMemberFilter,
  serviceUserFilter,
  setServiceUserFilter,
  eventTypeFilter,
  setEventTypeFilter,
  setPage,
  setSelectedRowKeys,
  nominative,
  hasActiveFilters,
  clearFilters
}: ReturnType<typeof useReconciliationFilters>) {
  const { t } = useTranslation()

  const { users } = useUsersList({ active: true })
  const { residents } = useResidentsList({})
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  const statusOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_STATUS_NEEDS_ATTENTION'),
        value: 'needs_attention'
      },
      { label: t('RECONCILIATION_STATUS_RECONCILED'), value: 'reconciled' }
    ],
    [t]
  )

  const discrepancyOptions = useMemo(
    () => [
      { label: t('RECONCILIATION_DISCREPANCY_LT5'), value: 'lt5' },
      { label: t('RECONCILIATION_DISCREPANCY_5TO15'), value: '5to15' },
      { label: t('RECONCILIATION_DISCREPANCY_15TO30'), value: '15to30' },
      { label: t('RECONCILIATION_DISCREPANCY_GT30'), value: 'gt30' }
    ],
    [t]
  )

  const eventTypeOptions = useMemo(() => {
    const all = [
      ...(shiftTypes ?? []).map((st: any) => ({
        label: st.name,
        value: st.id ?? st._id
      })),
      ...(visitTypes ?? []).map((vt: any) => ({
        label: vt.name,
        value: vt.id ?? vt._id
      }))
    ]
    return [...new Map(all.map((o) => [o.value, o])).values()]
  }, [shiftTypes, visitTypes])

  const resetPage = () => {
    setPage(1)
    setSelectedRowKeys([])
  }

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      return
    }
    if (!isValidReconciliationDateRange(dates[0], dates[1])) {
      return
    }
    setDateRange([dates[0], dates[1]])
    resetPage()
  }

  return (
    <FilterContainer title={t('FILTERS')}>
      <>
        <RangePicker
          value={dateRange}
          onChange={handleRangeChange}
          disabledDate={getReconciliationRangeDisabledDate}
          allowClear={false}
          format="DD-MM-YYYY"
          minDate={dayjs().subtract(RECONCILIATION_MAX_PAST_MONTHS, 'month')}
          maxDate={dayjs()}
        />
        <GroupedResidentsMultiSelect
          residents={residents}
          title={nominative}
          values={serviceUserFilter}
          handleInputChange={(selected) => {
            setServiceUserFilter(selected as string[])
            resetPage()
          }}
        />
        <GroupedUsersMultiSelect
          users={users}
          title={t('TEAM_MEMBERS')}
          values={teamMemberFilter}
          handleInputChange={(selected) => {
            setTeamMemberFilter(selected as string[])
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}
          values={eventTypeFilter}
          options={eventTypeOptions}
          hasClear
          hasSearch
          onChange={(selected) => {
            setEventTypeFilter(selected as string[])
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_TABLE_STATUS')}
          values={statusFilter ? [statusFilter] : []}
          options={statusOptions}
          onChange={(selected) => {
            const arr = selected as string[]
            setStatusFilter(arr.length ? arr[arr.length - 1] : undefined)
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_FILTER_DISCREPANCY')}
          values={discrepancyFilter}
          options={discrepancyOptions}
          hasClear
          onChange={(selected) => {
            setDiscrepancyFilter(selected as string[])
            resetPage()
          }}
        />
        {hasActiveFilters && (
          <ClearFiltersButton variant="text" onClick={clearFilters}>
            {t('CLEAR_FILTERS')}
          </ClearFiltersButton>
        )}
      </>
    </FilterContainer>
  )
}

const NarrowFilterMultiSelect = styled(MultiSelect)`
  min-width: 140px;
  width: 176px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    max-width: none;
  }
` as typeof MultiSelect

const ClearFiltersButton = styled(Button)`
  white-space: nowrap;
  align-self: center;
`
